import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors mb-8 font-medium"
      >
        <ArrowLeft size={18} />
        Back to Home
      </Link>

      <h1 className="headline text-4xl sm:text-5xl md:text-6xl text-[hsl(var(--navy))] mb-4">
        Terms &amp; Conditions
      </h1>
      <p className="text-[hsl(var(--charcoal))]/60 text-sm mb-10">
        Last Updated: September 2026
      </p>

      <div className="space-y-8 text-[hsl(var(--charcoal))]/80 leading-relaxed">
        {/* Section 1 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            1. Acceptance of Terms
          </h2>
          <p>
            By accessing and using the Naturals Concrete Construction ("NCC",
            "we", "our", "us") website, you agree to comply with these Terms and
            Conditions, as well as all applicable laws and regulations of the
            State of Arkansas.
          </p>
          <p className="mt-2">
            If you do not agree with these terms, please do not use our website
            or services.
          </p>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            2. Services Offered
          </h2>
          <p>NCC offers concrete construction services including, but not limited to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Concrete Driveways</li>
            <li>Foundations</li>
            <li>Concrete Slabs</li>
            <li>Concrete Flooring</li>
            <li>Patios &amp; Decks</li>
            <li>Concrete Demolition</li>
            <li>Sidewalks &amp; Walkways</li>
            <li>Repair &amp; Restoration</li>
          </ul>
          <p className="mt-2">
            All services are offered in the Northwest Arkansas and surrounding areas.
          </p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            3. Licensing &amp; Legal Requirements
          </h2>
          <p>
            NCC complies with all licensing requirements established by the{" "}
            <strong>Arkansas Contractors Licensing Board</strong> under Arkansas
            Code § 17-25-101 et seq.
          </p>
          <p className="mt-2">Our contractors:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Hold the required licenses from local, state, and federal authorities</li>
            <li>Maintain current liability insurance and workers&apos; compensation</li>
            <li>Comply with quality standards established by state law</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            4. Quotes &amp; Estimates
          </h2>
          <p>All quotes provided by NCC:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Are <strong>non-binding</strong> and do not create an obligation until a contract is signed</li>
            <li>Are <strong>valid for 30 days</strong> from the date of issuance</li>
            <li>Include a detailed description of the scope of work, materials, and timeline</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            5. Construction Contracts
          </h2>
          <p>All construction contracts with NCC:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Shall be <strong>in writing</strong> and signed by both parties</li>
            <li>Specify the scope of work, timeline, payment terms, and warranties</li>
            <li>Include change order clauses for project modifications</li>
            <li>Comply with the Arkansas Deceptive Trade Practices Act (ADTPA)</li>
          </ul>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            6. Payment Terms
          </h2>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Initial Deposit:</strong> A 30-50% deposit is required before work begins</li>
            <li><strong>Progress Payments:</strong> Additional payments are made according to the milestones in the contract</li>
            <li><strong>Final Payment:</strong> Due upon project completion and final inspection</li>
            <li><strong>Accepted Payment Methods:</strong> Cash, check, credit card, or bank transfer</li>
            <li><strong>Late Payment:</strong> A 1.5% monthly interest will apply to overdue balances</li>
          </ul>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            7. Intellectual Property
          </h2>
          <p>
            All content on the NCC website (including text, images, logos, and
            designs) is the property of Naturals Concrete Construction and is
            protected by United States copyright laws.
          </p>
          <p className="mt-2">You may not:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Reproduce, distribute, or modify our content without authorization</li>
            <li>Use our logos or trademarks without prior written consent</li>
            <li>Copy the design or structure of our website</li>
          </ul>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            8. Privacy &amp; Data Protection
          </h2>
          <p>NCC complies with applicable privacy laws, including:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Data Collection:</strong> We only collect information necessary to provide our services (name, phone, email, address)</li>
            <li><strong>Data Usage:</strong> We use your information to complete transactions, communicate about your project, and improve our services</li>
            <li><strong>Cookies:</strong> Our website may use cookies to enhance user experience; you may opt out</li>
            <li>We do <strong>not</strong> share your personal information with third parties without your consent, except as required by law</li>
          </ul>
          <p className="mt-2">
            In accordance with Arkansas Code § 25-1-114, we maintain clear and
            accessible privacy policies.
          </p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            9. Limitation of Liability
          </h2>
          <p>NCC shall not be liable for:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Indirect, incidental, or consequential damages</li>
            <li>Delays caused by adverse weather conditions or events beyond our control</li>
            <li>Damage to existing property not covered by insurance</li>
          </ul>
          <p className="mt-2">
            NCC&apos;s total liability for any claim related to our services
            shall not exceed the total amount paid by the client for the
            specific project.
          </p>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            10. Warranties
          </h2>
          <p>NCC offers the following warranties under Arkansas law:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Labor Warranty:</strong> 1 year from the date of project completion</li>
            <li><strong>Material Warranty:</strong> According to the manufacturer&apos;s warranty</li>
            <li><strong>Warranty Response:</strong> NCC will respond to any warranty issues within 10 calendar days of written notification</li>
          </ul>
        </section>

        {/* Section 11 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            11. Dispute Resolution
          </h2>
          <p>In the event of a dispute:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Negotiation:</strong> The parties will attempt to resolve the dispute through good faith negotiation</li>
            <li><strong>Mediation:</strong> If negotiation fails, the parties agree to submit the dispute to mediation in Washington County, Arkansas</li>
            <li><strong>Arbitration:</strong> If mediation is unsuccessful, the dispute will be resolved through binding arbitration under the rules of the American Arbitration Association</li>
            <li><strong>Jurisdiction:</strong> Any legal action shall be filed in the courts of Washington County, Arkansas, and shall be governed by the laws of the State of Arkansas</li>
          </ul>
        </section>

        {/* Section 12 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            12. Contract Termination
          </h2>
          <p>
            <strong>By the Client:</strong> The client may terminate the contract
            within 3 business days of signing under Arkansas consumer protection
            laws. Termination after this period may result in forfeiture of the
            deposit.
          </p>
          <p className="mt-2">
            <strong>By NCC:</strong> NCC may terminate the contract if the client
            fails to meet payment terms or commits a material breach.
          </p>
        </section>

        {/* Section 13 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            13. Modifications
          </h2>
          <p>
            NCC reserves the right to modify these Terms and Conditions at any
            time. Changes will be effective immediately upon posting on our
            website. It is the user&apos;s responsibility to review these terms
            periodically.
          </p>
        </section>

        {/* Section 14 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            14. Governing Law
          </h2>
          <p>
            These Terms and Conditions shall be governed by and construed in
            accordance with the laws of the State of Arkansas, without regard to
            its conflict of law provisions.
          </p>
        </section>

        {/* Section 15 */}
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            15. Contact Information
          </h2>
          <p>For questions about these Terms and Conditions, please contact us:</p>
          <div className="mt-3 space-y-2">
            <p><strong>Naturals Concrete Construction</strong></p>
            <p>📧 <a href="mailto:naturalconcrete.contruction@gmail.com" className="text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors">
              naturalconcrete.contruction@gmail.com
            </a></p>
            <p>📞 <a href="tel:+14794227180" className="text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors">
              479-422-7180
            </a></p>
            <p>📍 Northwest Arkansas &amp; Surrounding Areas</p>
          </div>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-[hsl(var(--concrete))] text-center text-[hsl(var(--charcoal))]/50 text-sm">
        &copy; {new Date().getFullYear()} Naturals Concrete Construction. All rights reserved.
      </div>
    </div>
  );
}